import { type FC, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';

import type { FilesFileSetSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/files';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import { selectFilesByAssetId } from '@iconik-io/shared-lib/src/redux/entities/selectors/files';
import { CLOUD_STORAGE_METHODS } from '@iconik-io/shared-lib/src/utils/constants/storages';

import { Link } from '../../components/link/link';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import {
  MANAGE_MEDIA_FORMATS,
  MANAGE_OTHER_FORMATS,
} from '../../constants/help_urls';
import { FORMATS_PANEL_FILESETS_FILES } from '../../constants/modals';
import {
  SHARE_ASSET_PAGE_ROUTE,
  SHARE_ASSET_VERSION_PAGE_ROUTE,
} from '../../constants/routes';
import { cleanupDeletedFileSets } from '../../ducks/file_sets/thunks';
import { getAssetFilesetsForFilesPanel } from '../../ducks/files/thunks';
import { errorMessage } from '../../ducks/messages/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  selectAsset,
  selectClosedAssetProxies,
} from '../../ducks/pages/asset/selectors';
import { requestOriginal } from '../../ducks/pages/asset/thunks';
import { selectIsLatestVersion } from '../../ducks/version/selectors';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import GearBoxActions from '../../ui/gearbox_actions/gearbox_actions';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import { selectUserRoles } from '../../utils/get_user_roles';
import { ui } from '../../utils/helpers';
import { getSearchItemMediaType } from '../../utils/media_type';
import UploadContextMenu from './context_menu_upload';
import {
  selectCanRequestOriginal,
  selectMediaFormats,
  selectNonMediaFormats,
  selectResolvedPermissions,
  selectSubtitles,
  selectSubtitlesFormats,
  selectUploading,
  selectUploadingProxy,
} from './selectors';
import TableFormats from './table_formats';
import TableProxies from './table_proxies';
import TableSubtitles from './table_subtitles';

import emptyKeyframesSvg from '../../images/empty_keyframes.svg';

import styles from './asset_files.module.css';

/**
 * Optional prop readOnly - if truthy, it will strip Files Panel of any actions such as
 * downloading, uploading, deleting or href.
 */
type Props = {
  readOnly?: boolean;
};

const UploadNewButton: FC = () => (
  <Button noBorder iconName="load_up" {...ui('upload-new')}>
    Upload new
  </Button>
);

const FilesPanel: FC<Props> = (props) => {
  const [originalRequestedAssetIds, setOriginalRequestedAssetIds] = useState<
    Set<string>
  >(new Set());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const dispatch = useAppDispatch();

  // Route and params come from the same match: see PanelsContainer.
  const { route, params } = useLeafRouteParams();
  const assetId = params.id;
  const versionId = params.versionId;
  const asset = useAppSelector((state) => selectAsset(state, assetId));

  const subtitlesFormats = useAppSelector((state) =>
    selectSubtitlesFormats(state, assetId, versionId),
  );
  const mediaFormats = useAppSelector((state) =>
    selectMediaFormats(state, assetId, versionId),
  );
  const nonMediaFormats = useAppSelector((state) =>
    selectNonMediaFormats(state, assetId, versionId),
  );
  const files = useAppSelector((state) => selectFilesByAssetId(state, assetId));
  const subtitles = useAppSelector((state) => selectSubtitles(state));
  const permissions = useAppSelector((state) =>
    selectResolvedPermissions(state, assetId),
  );
  const canRequestOriginal = useAppSelector((state) =>
    selectCanRequestOriginal(state, assetId, versionId),
  );
  const uploading = useAppSelector((state) => selectUploading(state));
  const uploadingProxy = useAppSelector((state) => selectUploadingProxy(state));
  const isLatestVersion = useAppSelector((state) =>
    selectIsLatestVersion(state, assetId, versionId),
  );
  const { webCanDownloadOriginal, webCanDownloadProxy } = useAppSelector(
    (state) => selectUserRoles(state),
  );

  const assetProxies = useAppSelector((state) =>
    selectClosedAssetProxies(state, assetId, versionId),
  ).filter((proxy) => proxy.asset_id === assetId);

  const loading = useAppSelector(
    (state) => state.filesReducer.loadingAssetFormatsData || !asset,
  );
  const canWrite =
    permissions.write &&
    ![SHARE_ASSET_PAGE_ROUTE, SHARE_ASSET_VERSION_PAGE_ROUTE].includes(route);

  useEffect(() => {
    if (!assetId) {
      return;
    }
    setSelectedIds([]);
    dispatch(cleanupDeletedFileSets());
    dispatch(getAssetFilesetsForFilesPanel(assetId, versionId));
  }, [assetId, versionId]);

  useEffect(
    () => () => {
      dispatch(cleanupDeletedFileSets());
    },
    [],
  );

  const handleRequestOriginal = () => {
    if (!assetId) {
      return;
    }
    setOriginalRequestedAssetIds((prev) => new Set(prev).add(assetId));

    const isgFormat = [...mediaFormats, ...nonMediaFormats].find(
      (format) =>
        format.name === 'ORIGINAL' &&
        !format.storage_methods?.some((method) =>
          CLOUD_STORAGE_METHODS.includes(method),
        ),
    );
    if (isgFormat) {
      dispatch(requestOriginal(isgFormat.id));
      return;
    }
    dispatch(errorMessage({ text: "Can't request original" }));
  };

  const noContent =
    !mediaFormats.length &&
    !nonMediaFormats.length &&
    !subtitlesFormats.length &&
    !assetProxies.length &&
    !loading;
  let buttons;

  if (!props.readOnly) {
    buttons =
      asset && isLatestVersion ? (
        <GearBoxActions
          triggerElement={<UploadNewButton />}
          contextItems={[asset]}
          contextMenu={UploadContextMenu}
        />
      ) : (
        <small>
          {asset ? (
            <>
              Go to the &nbsp;
              <Link to={`/asset/${asset?.id}`}>latest version</Link>
              &nbsp; to upload a new one
            </>
          ) : undefined}
        </small>
      );
  }

  const canDownloadOriginal = webCanDownloadOriginal && !asset?.is_blocked;
  const canDownloadProxy = webCanDownloadProxy && !asset?.is_blocked;

  const handleShowFilesClick = useCallback(
    (fileSet: FilesFileSetSchemaWithIdNullable) => {
      dispatch(
        openModal({
          id: FORMATS_PANEL_FILESETS_FILES,
          contextItems: [fileSet],
          options: {
            canDownloadOriginal,
            readOnly: props.readOnly,
          },
        }),
      );
    },
    [canDownloadOriginal, props.readOnly],
  );

  const searchItem = {
    ...asset,
    object_type: OBJECT_TYPE_ASSETS,
    formats: mediaFormats,
    files,
  };

  const mediaType = getSearchItemMediaType(searchItem);

  return (
    <div className={styles.root}>
      {noContent || !asset ? (
        <NoContentPanel
          title="No files available"
          imageSrc={emptyKeyframesSvg}
          onActionClick={() => {}}
          actionElement={canWrite ? buttons : undefined}
        />
      ) : (
        <>
          {loading && <LoadingBar className={styles.loading} />}
          <div
            className={classNames(styles.groups, {
              [styles.groups_readonly]: props.readOnly,
            })}
          >
            <TableFormats
              title="MEDIA FORMATS"
              help={MANAGE_MEDIA_FORMATS}
              items={mediaFormats}
              uploading={uploading}
              files={files}
              canDownload={canDownloadOriginal}
              asset={asset.id}
              readOnly={props.readOnly}
              onShowFilesClick={handleShowFilesClick}
              uiKey="media-formats"
              mediaType={mediaType}
              selectedIds={selectedIds}
              onSelectIds={setSelectedIds}
            />
            <TableFormats
              help={MANAGE_OTHER_FORMATS}
              title={mediaFormats.length ? 'OTHER FORMATS' : 'FORMATS'}
              items={nonMediaFormats}
              uploading={uploading}
              files={files}
              canDownload={canDownloadOriginal}
              asset={asset.id}
              onShowFilesClick={handleShowFilesClick}
              uiKey="non-media-formats"
              readOnly={props.readOnly}
              mediaType={mediaType}
              selectedIds={selectedIds}
              onSelectIds={setSelectedIds}
            />
            <TableProxies
              title="PROXIES"
              items={assetProxies}
              canDownload={canDownloadProxy}
              assetId={asset.id}
              uploading={uploadingProxy}
              readOnly={props.readOnly}
              selectedIds={selectedIds}
              onSelectIds={setSelectedIds}
            />
            <TableSubtitles
              items={subtitlesFormats}
              subtitles={subtitles}
              uploading={uploading}
              files={files}
              canDownload={canDownloadOriginal}
              assetId={asset.id}
              onShowFilesClick={handleShowFilesClick}
              readOnly={props.readOnly}
              selectedIds={selectedIds}
              onSelectIds={setSelectedIds}
            />
          </div>
          <div className={styles.sticky_buttons}>
            {!loading && canWrite && buttons}
            {canRequestOriginal &&
              !!assetId &&
              !originalRequestedAssetIds.has(assetId) && (
                <Button
                  variant="success"
                  noBorder
                  onClick={handleRequestOriginal}
                >
                  Request Original
                </Button>
              )}
          </div>
        </>
      )}
    </div>
  );
};

export default FilesPanel;
